// @ts-strict-ignore
import {Component, PanelLifecycleContext} from 'panel';

import {Bucket, StringBucketType} from 'common/widgets/bucket-pane/types';
import {MetricEntry, QueryEventEntry} from 'common/query-builder/query-entry-section/types';
import {FullLengthDateRange} from 'common/widgets/date-range-picker/models/full-length-date-range';
import {DateRangeAttributes} from 'common/widgets/date-range-picker/models/types';
import {ComparisonDate} from 'common/query-builder/query-block/time-comparison-menu/types';
import {DashboardOrigin} from 'common/report/util/bookmark-overrides';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {isEventSegment, Segment} from 'common/query-builder/query-block/segment-menu/types';
import {Segmentation} from 'common/types/reports/query';
import {ConversionWindow, CountType} from 'common/report/funnels/models/types';
import type {BookmarkTheme} from 'common/report/queries/app-api/themes';

import {CUSTOM_ATTRIBUTION_MAX_RANGE} from './report/constants';
import {BookmarkType} from './report/queries/app-api/bookmark-client';
import {getProjectId} from './report/util';
import {ChartType, MetricType, XAU_MATH_TYPES} from './types/reports/bookmark';
import {isMultiAttributionBehavior} from './query-builder/query-entry-section/row-types/behavior/utils';
import {DAY_CHART_MAX_DAY_DURATION} from './report/insights/models/constants';
import {isDateLimitsEnabled} from './report/util/feature-sampling';
import {TranslationKey} from './i18n';

export type AdditionalContext = Partial<MultimetricContext> &
  Partial<ViewAsFlowsContext> &
  Partial<ViewAsFunnelContext>;

export type QueryContext = {
  bookmarkType: BookmarkType;
  dataGroupId?: string;
  dateRange?: DateRangeAttributes;
  events?: QueryEventEntry[];
  metrics?: MetricEntry[];
  comparedDateRange?: ComparisonDate;
  originDashboard?: DashboardOrigin;
  segments: Segment[];
  filters: PropertyFilter[];
  conversionCriteria?: ConversionCriteria;
  querySamplingEnabled?: boolean;
  theme?: BookmarkTheme;
} & AdditionalContext;

export type SegmentationMetadata = {
  segment: Segment;
  numericBucket?: Bucket;
};

export type ConversionCriteria = {
  conversionWindow: ConversionWindow;
  countType: CountType;
  exclusionSteps: QueryEventEntry[];
  holdingPropertyConstant: Segmentation[];
};

export type MultimetricContext = {
  metricType: MetricType;
  chartType: ChartType;
};

export type ViewAsFlowsContext = {
  viewAsFlowsContext: {funnelStep: number};
};

export type ViewAsFunnelContext = {
  viewAsFunnelContext: {topPathsFlowSteps: string[]; selectedSteps: Set<number>};
};

export function isViewAsFunnelContext(additionalContext: AdditionalContext): additionalContext is ViewAsFunnelContext {
  return !!(additionalContext as ViewAsFunnelContext)?.viewAsFunnelContext;
}

const DEFAULT_QUERY_CONTEXT: QueryContext = {
  bookmarkType: null,
  dataGroupId: null,
  dateRange: new FullLengthDateRange().getAttributes(),
  events: [],
  comparedDateRange: null,
  originDashboard: null,
  segments: [],
  filters: [],
  conversionCriteria: null,
  querySamplingEnabled: false,
};

type GroupingSegment = {
  segmentIdx: number;
  initialType?: StringBucketType;
};

export class QueryContextManager implements PanelLifecycleContext {
  private connectedComponents: Set<Component<any>> = new Set();
  public isDirty = false;
  public query: QueryContext = DEFAULT_QUERY_CONTEXT;
  public segmentationMetadatas: SegmentationMetadata[] = [];
  public canEditBoard = false;
  public groupingSegment: GroupingSegment | null = null;

  public updateQuery(query: Partial<QueryContext>, preventUpdate = false) {
    this.isDirty = true;
    this.query = {
      bookmarkType: this.query.bookmarkType,
      segments: this.query.segments,
      filters: this.query.filters,
      ...query,
    };

    if (preventUpdate === false) {
      this.updateComponents();
    }
  }

  public updateBoardPermissions(canEditBoard: boolean) {
    this.canEditBoard = canEditBoard;
    this.updateComponents();
  }

  public reset() {
    this.isDirty = false;
    this.query = DEFAULT_QUERY_CONTEXT;
  }

  public updateSegmentationMetadatas(segmentationMetadatas: SegmentationMetadata[]) {
    this.segmentationMetadatas = segmentationMetadatas;
    this.updateComponents();
  }

  /**
   * Starts grouping data for a segment.
   */
  public openGroupDataModal(segmentIdx: number, initialType?: StringBucketType) {
    this.groupingSegment = {segmentIdx, initialType};
    this.updateComponents();
  }

  /**
   * Closes the group data modal.
   */
  public closeGroupDataModal() {
    this.groupingSegment = null;
    this.updateComponents();
  }

  // Panel Context lifecycle methods
  public bindToComponent(component: Component<any, any, any, any, any>): void {
    this.connectedComponents.add(component);
  }

  public unbindFromComponent(component: Component<any, any, any, any, any>): void {
    this.connectedComponents.delete(component);
  }

  private updateComponents() {
    for (const component of this.connectedComponents) {
      component.update();
    }
  }

  /** @todo pipe project settings here */
  public getMaxQueryDaysAndReason(): {maxQueryDays: number | null; reasonKey: TranslationKey | null} {
    const DEFAULT_MAX_QUERY_DAYS_REASON_KEY = `reports.date-range.disabled.max-days`;

    const DEFAULT_ATTRIBUTION_LIMIT = 180;
    // for multi touch attribution queries, certain projects
    // need a limit on the number of days they can query
    // note that this is different from max history days we pass
    // in other components through project feature gates
    if (
      this.query.segments?.some(
        (segment: Segment) => isEventSegment(segment) && isMultiAttributionBehavior(segment.event.behavior),
      )
    ) {
      if (CUSTOM_ATTRIBUTION_MAX_RANGE[getProjectId()] !== undefined) {
        return {
          maxQueryDays: CUSTOM_ATTRIBUTION_MAX_RANGE[getProjectId()],
          reasonKey: DEFAULT_MAX_QUERY_DAYS_REASON_KEY,
        };
      }
      return {
        maxQueryDays: DEFAULT_ATTRIBUTION_LIMIT,
        reasonKey: DEFAULT_MAX_QUERY_DAYS_REASON_KEY,
      };
    }

    // As part of date limits, we add some additional limits on date ranges here
    if (
      isDateLimitsEnabled() &&
      this.query.metrics?.some((metric) => metric.measurement?.aggregationOperator === XAU_MATH_TYPES.DAU)
    ) {
      // XAU queries cannot have higher granularity units than the aggregation, so DAU queries cannot be week, month, or year units
      // With date limits, daily queries are also limited in date range, so we limit DAU queries to the same date range
      // Otherwise, these DAU queries would have no valid units
      return {
        maxQueryDays: DAY_CHART_MAX_DAY_DURATION,
        reasonKey: `reports.date-range.disabled.dau`,
      };
    }

    return {
      maxQueryDays: null,
      reasonKey: null,
    };
  }
}
